import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiAlertTriangle,
  FiArrowDown,
  FiArrowLeft,
  FiArrowUp,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiDownload,
  FiEye,
  FiImage,
  FiPackage,
  FiPlus,
  FiShield,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import Swal from "sweetalert2";
import MenuLateral from "../../Components/MenuLateral";
import AuthenticatedMedia from "../../Components/AuthenticatedMedia";
import { LoginContext } from "../../context/LoginContext";
import { USER_ROLES } from "../../auth/session";
import { APP_ROUTES } from "../../auth/access";
import api from "../../services/api";
import "./index.css";

const PAGE_SIZE = 48;
const EDITABLE_STATUSES = new Set([
  "AWAITING_SELECTION",
  "ADJUSTMENT_REQUESTED",
]);

const SELECTION_STATUS = {
  AWAITING_SELECTION: "Em montagem",
  ADJUSTMENT_REQUESTED: "Ajuste solicitado",
  SELECTION_SENT: "Enviada para revisão",
  SELECTION_APPROVED: "Seleção aprovada",
};

const COMMERCIAL_STATUS = {
  PEDIDO_ORCADO: "Pedido orçado",
  PEDIDO_REALIZADO: "Pedido realizado",
  VENDA_CONCLUIDA: "Venda concluída",
  PEDIDO_CANCELADO: "Pedido cancelado",
};

const getErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "string") return message;
  return error?.message || "Não foi possível concluir a operação.";
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "Data não informada";

const productName = (item) =>
  item?.productSnapshot?.name || item?.productName || "Produto";

const photoName = (photo, fallback = "Foto") =>
  photo?.originalName || photo?.photo?.originalName || fallback;

const photoIdOf = (photo) =>
  photo?.photoId || photo?.eventPhotoId || photo?.photo?.photoId || photo?.photo?.eventPhotoId;

const thumbnailPath = (photo) =>
  photo?.media?.thumbnail?.path || photo?.photo?.media?.thumbnail?.path;

const previewPath = (photo) =>
  photo?.media?.preview?.path || photo?.photo?.media?.preview?.path;

const activeEventsFor = (item) => (item?.events || []).filter((event) => event.active);

const photoAudienceLabel = (photo, personalized) => {
  const categories = new Set(photo?.categories || []);
  if (categories.has("MINE") && categories.has("GENERAL")) {
    return personalized ? "Para você e para o evento" : "Match do comprador e foto geral";
  }
  if (categories.has("MINE")) return personalized ? "Reconheceu você" : "Match do comprador";
  if (categories.has("GENERAL")) return "Foto geral";
  return "Foto autorizada";
};

const GALLERY_FILTERS = [
  { value: "", label: "Todas" },
  { value: "MINE", label: "Minhas fotos" },
  { value: "GENERAL", label: "Fotos gerais" },
];

function SelecaoFotos() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { LoggedUser } = useContext(LoginContext);
  const isAdmin = LoggedUser?.role === USER_ROLES.ADMIN;
  const isUser = LoggedUser?.role === USER_ROLES.USER;
  const saleId = searchParams.get("saleId") || "";
  const itemId = searchParams.get("itemId") || "";
  const eventId = searchParams.get("eventId") || "";

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [galleryPage, setGalleryPage] = useState(1);
  const [galleryTotalPages, setGalleryTotalPages] = useState(0);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [galleryCategory, setGalleryCategory] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewSource, setPreviewSource] = useState(null);
  const previewCloseRef = useRef(null);
  const previewDialogRef = useRef(null);
  const previewTriggerRef = useRef(null);
  const requestVersionRef = useRef(0);

  const activeSale = useMemo(
    () => orders.find((order) => order.id === saleId) || null,
    [orders, saleId],
  );
  const activeItem = useMemo(
    () => activeSale?.items?.find((item) => item.id === itemId) || null,
    [activeSale, itemId],
  );
  const products = useMemo(
    () => orders.flatMap((order) =>
      (order.items || []).map((item) => ({ order, item })),
    ),
    [orders],
  );
  const linkedEvents = useMemo(() => activeEventsFor(activeItem), [activeItem]);
  const activeEvent = linkedEvents.find((event) => event.eventId === eventId) || null;
  const previewEventName =
    preview?.eventName || preview?.event?.name || activeEvent?.eventName || "Evento";
  const previewEventDate =
    preview?.eventDate || preview?.event?.eventDate || activeEvent?.eventDate;
  const selectionStatus = summary?.selectionStatus || activeItem?.selectionStatus || "";
  const editable = EDITABLE_STATUSES.has(selectionStatus);
  const selected = useMemo(() => summary?.selections || [], [summary]);
  const selectedPhotoIds = useMemo(
    () => new Set(selected.map((selection) => selection.eventPhotoId)),
    [selected],
  );
  const previewItems = previewSource === "selected" ? selected : gallery;
  const previewIndex = preview
    ? previewItems.findIndex((item) => photoIdOf(item) === photoIdOf(preview))
    : -1;
  const selectedCount = Number(summary?.selectedPhotoCount || 0);
  const requiredCount = Number(summary?.requiredPhotoCount ?? activeItem?.requiredPhotoCount ?? 0);
  const remainingCount = Number(
    summary?.remainingPhotoCount ?? Math.max(0, requiredCount - selectedCount),
  );
  const selectionProgress = requiredCount > 0
    ? Math.min(100, Math.round((selectedCount / requiredCount) * 100))
    : 0;
  const pendingEventCount = Number(
    summary?.pendingLinkedEventCount ?? summary?.pendingEventIds?.length ?? 0,
  );
  const canAddMore = selectedCount < requiredCount;
  const canEditCurrentEvent = editable;

  const updateSelectionUrl = useCallback(
    ({ nextSaleId = saleId, nextItemId = itemId, nextEventId = eventId }, replace = false) => {
      const next = new URLSearchParams();
      if (nextSaleId) next.set("saleId", nextSaleId);
      if (nextItemId) next.set("itemId", nextItemId);
      if (nextEventId) next.set("eventId", nextEventId);
      setSearchParams(next, { replace });
    },
    [eventId, itemId, saleId, setSearchParams],
  );

  useEffect(() => {
    let active = true;
    const loadOrders = async () => {
      setOrdersLoading(true);
      setOrdersError("");
      try {
        if (isAdmin) {
          if (!saleId) {
            setOrders([]);
            return;
          }
          const response = await api.get(`/sales/${saleId}`);
          if (active) setOrders(response.data?.order ? [response.data.order] : []);
        } else {
          const response = await api.get("/sales/mine", {
            params: { page: 1, limit: 100 },
          });
          if (active) setOrders(response.data?.orders || []);
        }
      } catch (error) {
        if (active) {
          setOrders([]);
          setOrdersError(getErrorMessage(error));
        }
      } finally {
        if (active) setOrdersLoading(false);
      }
    };
    loadOrders();
    return () => {
      active = false;
    };
  }, [isAdmin, saleId]);

  const requestSummary = useCallback(async () => {
    if (!activeSale || !activeItem) return null;
    const response = await api.get(
      `/sales/${activeSale.id}/items/${activeItem.id}/selections`,
    );
    return response.data;
  }, [activeItem, activeSale]);

  const requestGalleryPages = useCallback(async (targetEventId, pageCount = 1) => {
    if (!activeSale || !activeItem || !targetEventId) {
      return { photos: [], page: 1, totalPages: 0 };
    }
    const pages = Array.from({ length: Math.max(1, pageCount) }, (_, index) => index + 1);
    const responses = await Promise.all(
      pages.map((page) => api.get(
        `/sales/${activeSale.id}/items/${activeItem.id}/gallery`,
        {
          params: {
            eventId: targetEventId,
            page,
            limit: PAGE_SIZE,
            ...(galleryCategory ? { category: galleryCategory } : {}),
          },
        },
      )),
    );
    const last = responses[responses.length - 1]?.data || {};
    return {
      photos: responses.flatMap((response) => response.data?.photos || []),
      page: Number(last.pagination?.page || pages.length),
      totalPages: Number(last.pagination?.totalPages || 0),
    };
  }, [activeItem, activeSale, galleryCategory]);

  useEffect(() => {
    if (!activeItem) {
      setSummary(null);
      setGallery([]);
      setGalleryError("");
      return;
    }
    const publishedEvents = linkedEvents.filter(
      (event) => event.publicationStatus === "PUBLISHED",
    );
    const requestedEvent = publishedEvents.find((event) => event.eventId === eventId);
    const nextEventId = requestedEvent?.eventId || publishedEvents[0]?.eventId || "";
    if (nextEventId !== eventId) {
      updateSelectionUrl({ nextEventId }, true);
      return;
    }

    let active = true;
    const version = ++requestVersionRef.current;
    const loadContext = async () => {
      setSummaryLoading(true);
      setGalleryLoading(Boolean(nextEventId));
      setGalleryError("");
      try {
        const mayLoadGallery = isUser && EDITABLE_STATUSES.has(activeItem.selectionStatus) && nextEventId;
        const [nextSummary, galleryResult] = await Promise.all([
          requestSummary(),
          mayLoadGallery
            ? requestGalleryPages(nextEventId, 1)
                .then((value) => ({ value }))
                .catch((error) => ({ error }))
            : Promise.resolve({ value: null }),
        ]);
        if (!active || version !== requestVersionRef.current) return;
        setSummary(nextSummary);
        const nextEditable = EDITABLE_STATUSES.has(nextSummary?.selectionStatus);
        if (nextEditable && nextEventId) {
          if (galleryResult.error) throw galleryResult.error;
          const nextGallery = galleryResult.value || await requestGalleryPages(nextEventId, 1);
          if (!active || version !== requestVersionRef.current) return;
          setGallery(nextGallery.photos);
          setGalleryPage(nextGallery.page);
          setGalleryTotalPages(nextGallery.totalPages);
        } else {
          setGallery([]);
          setGalleryPage(1);
          setGalleryTotalPages(0);
        }
      } catch (error) {
        if (active && version === requestVersionRef.current) {
          setSummary(null);
          setGallery([]);
          setGalleryError(getErrorMessage(error));
        }
      } finally {
        if (active && version === requestVersionRef.current) {
          setSummaryLoading(false);
          setGalleryLoading(false);
        }
      }
    };
    loadContext();
    return () => {
      active = false;
    };
  }, [activeItem, eventId, isUser, linkedEvents, requestGalleryPages, requestSummary, updateSelectionUrl]);

  const reconcile = async ({ keepPages = true } = {}) => {
    const nextSummary = await requestSummary();
    setSummary(nextSummary);
    if (EDITABLE_STATUSES.has(nextSummary?.selectionStatus) && eventId) {
      const nextGallery = await requestGalleryPages(
        eventId,
        keepPages ? galleryPage : 1,
      );
      setGallery(nextGallery.photos);
      setGalleryPage(nextGallery.page);
      setGalleryTotalPages(nextGallery.totalPages);
    } else {
      setGallery([]);
      setGalleryPage(1);
      setGalleryTotalPages(0);
    }
  };

  const runAction = async (key, operation, success) => {
    setActionLoading(key);
    try {
      await operation();
      await reconcile();
      if (success) await Swal.fire(success.title, success.text, "success");
    } catch (error) {
      if (error?.response?.status === 409) {
        try {
          await reconcile();
        } catch {
          // Mantém o erro comercial original; a próxima carga tentará reconciliar novamente.
        }
      }
      await Swal.fire("Não foi possível continuar", getErrorMessage(error), "error");
    } finally {
      setActionLoading("");
    }
  };

  const selectProduct = (order, item) => {
    const firstPublishedEvent = activeEventsFor(item).find(
      (event) => event.publicationStatus === "PUBLISHED",
    );
    updateSelectionUrl({
      nextSaleId: order.id,
      nextItemId: item.id,
      nextEventId: firstPublishedEvent?.eventId || "",
    });
  };

  const selectEvent = (nextEventId) => {
    if (nextEventId === eventId) return;
    setGallery([]);
    setGalleryPage(1);
    setGalleryLoading(true);
    setGalleryError("");
    updateSelectionUrl({ nextEventId });
  };

  const selectGalleryCategory = (nextCategory) => {
    if (nextCategory === galleryCategory) return;
    setGallery([]);
    setGalleryPage(1);
    setGalleryLoading(true);
    setGalleryError("");
    setGalleryCategory(nextCategory);
  };

  const addPhoto = (eventPhotoId) => runAction(
    `add:${eventPhotoId}`,
    () => api.post(
      `/sales/${activeSale.id}/items/${activeItem.id}/selections`,
      { eventPhotoId },
    ),
  );

  const removePhoto = (selection) => runAction(
    `remove:${selection.eventPhotoId}`,
    () => api.delete(
      `/sales/${activeSale.id}/items/${activeItem.id}/selections/${selection.eventPhotoId}`,
    ),
  );

  const movePhoto = (selectionId, direction) => {
    const currentIndex = selected.findIndex((item) => item.selectionId === selectionId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= selected.length) return;
    const reordered = [...selected];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(nextIndex, 0, moved);
    runAction(
      `move:${selectionId}`,
      () => api.patch(
        `/sales/${activeSale.id}/items/${activeItem.id}/selections/reorder`,
        {
          items: reordered.map((item, index) => ({
            selectionId: item.selectionId,
            position: index + 1,
          })),
        },
      ),
    );
  };

  const submitSelection = async () => {
    const confirmation = await Swal.fire({
      title: "Confirmar seleção?",
      text: `Você está enviando exatamente ${requiredCount} foto(s) para revisão.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirmar e enviar",
      cancelButtonText: "Continuar escolhendo",
    });
    if (!confirmation.isConfirmed) return;
    await runAction(
      "submit",
      () => api.post(
        `/sales/${activeSale.id}/items/${activeItem.id}/selections/submit`,
      ),
      { title: "Seleção enviada", text: "As fotos foram encaminhadas para revisão." },
    );
  };

  const reviewSelection = (nextStatus) => runAction(
    `review:${nextStatus}`,
    () => api.patch(
      `/sales/${activeSale.id}/items/${activeItem.id}/selection-status`,
      { selectionStatus: nextStatus },
    ),
    nextStatus === "SELECTION_APPROVED"
      ? { title: "Seleção aprovada", text: "A composição foi aprovada." }
      : { title: "Ajuste solicitado", text: "A galeria voltou a ficar disponível para edição." },
  );

  const downloadFile = async (endpoint, fallbackName) => {
    setActionLoading(`download:${endpoint}`);
    try {
      const response = await api.get(endpoint, { responseType: "blob" });
      const disposition = response.headers?.["content-disposition"] || "";
      const matchedName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      const objectUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = matchedName || fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      await Swal.fire("Download indisponível", getErrorMessage(error), "error");
    } finally {
      setActionLoading("");
    }
  };

  const loadMore = async () => {
    const nextPage = galleryPage + 1;
    setGalleryLoading(true);
    setGalleryError("");
    try {
      const next = await requestGalleryPages(eventId, nextPage);
      setGallery(next.photos);
      setGalleryPage(next.page);
      setGalleryTotalPages(next.totalPages);
    } catch (error) {
      setGalleryError(getErrorMessage(error));
    } finally {
      setGalleryLoading(false);
    }
  };

  const openPreview = (photo, trigger, source = "gallery") => {
    previewTriggerRef.current = trigger;
    setPreviewSource(source);
    setPreview(photo);
  };

  const closePreview = useCallback(() => {
    setPreview(null);
    setPreviewSource(null);
    window.setTimeout(() => previewTriggerRef.current?.focus(), 0);
  }, []);

  const navigatePreview = useCallback((direction) => {
    const currentIndex = previewItems.findIndex(
      (item) => photoIdOf(item) === photoIdOf(preview),
    );
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= previewItems.length) return false;
    setPreview(previewItems[nextIndex]);
    return true;
  }, [preview, previewItems]);

  useEffect(() => {
    if (!preview) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    previewCloseRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePreview();
        return;
      }
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const direction = event.key === "ArrowLeft" ? -1 : 1;
        if (navigatePreview(direction)) event.preventDefault();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        previewDialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ) || [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePreview, navigatePreview, preview]);

  const protectedImageProps = {
    draggable: false,
    onContextMenu: (event) => event.preventDefault(),
    onDragStart: (event) => event.preventDefault(),
  };

  const renderProductChoice = () => {
    if (isAdmin && !saleId) {
      return (
        <div className="photo-selection-guidance">
          <FiArrowLeft aria-hidden="true" />
          <div>
            <h2>Abra um pedido em Vendas</h2>
            <p>A revisão administrativa começa pelo item do pedido, mantendo o comprador e os vínculos corretos.</p>
            <button type="button" onClick={() => navigate(APP_ROUTES.SALES)}>Voltar para Vendas</button>
          </div>
        </div>
      );
    }
    if (ordersLoading) return <div className="photo-selection-state" role="status">Carregando produtos disponíveis...</div>;
    if (ordersError) return <div className="photo-selection-state is-error" role="alert">{ordersError}</div>;
    if (!products.length) return <div className="photo-selection-state">Nenhum produto com seleção de fotos está disponível.</div>;
    return (
      <div className="photo-product-grid">
        {products.map(({ order, item }) => {
          const current = order.id === saleId && item.id === itemId;
          return (
            <button
              key={`${order.id}:${item.id}`}
              type="button"
              className={`photo-product-card${current ? " is-active" : ""}`}
              onClick={() => selectProduct(order, item)}
              aria-pressed={current}
            >
              <span className="photo-product-icon"><FiPackage aria-hidden="true" /></span>
              <span className="photo-product-copy">
                <strong>{productName(item)}</strong>
                <small>{COMMERCIAL_STATUS[order.commercialStatus] || order.commercialStatus}</small>
              </span>
              <span className="photo-product-quota">{item.requiredPhotoCount} foto(s)</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="photo-selection-page">
      <MenuLateral />
      <main className="photo-selection-main">
        <header className="photo-selection-header">
          <div>
            <span className="photo-selection-kicker">
              {activeItem ? productName(activeItem) : "Produto"}
              <span aria-hidden="true"> › </span>
              {activeEvent?.eventName || "Evento"}
              <span aria-hidden="true"> › Fotos</span>
            </span>
            <h1>Seleção de fotos</h1>
            <p>Monte a composição contratada usando a galeria autorizada de cada evento.</p>
          </div>
          <div className="photo-protection-note">
            <FiShield aria-hidden="true" />
            <span><strong>Visualização protegida</strong>Preview ampliado sempre com marca d’água.</span>
          </div>
        </header>

        <section className="photo-selection-section" aria-labelledby="products-title">
          <div className="photo-section-heading">
            <span className="photo-step-number">01</span>
            <div><h2 id="products-title">Produtos disponíveis</h2><p>Escolha o produto cuja composição você deseja montar ou revisar.</p></div>
          </div>
          {renderProductChoice()}
        </section>

        {activeItem && (
          <section className="photo-selection-section" aria-labelledby="events-title">
            <div className="photo-section-heading">
              <span className="photo-step-number">02</span>
              <div><h2 id="events-title">Eventos vinculados</h2><p>Eventos pendentes permanecem visíveis, mas ainda não liberam fotos.</p></div>
            </div>
            <div className="photo-event-list">
              {linkedEvents.map((event) => {
                const published = event.publicationStatus === "PUBLISHED";
                const current = event.eventId === eventId;
                return (
                  <button
                    key={event.eventId}
                    type="button"
                    className={`photo-event-card${current ? " is-active" : ""}${published ? "" : " is-pending"}`}
                    onClick={() => published && selectEvent(event.eventId)}
                    disabled={!published}
                    aria-pressed={published ? current : undefined}
                  >
                    <FiCalendar aria-hidden="true" />
                    <span><strong>{event.eventName || "Evento"}</strong><small>{formatDate(event.eventDate)}</small></span>
                    <em>{published ? "Publicado" : "Aguardando publicação"}</em>
                  </button>
                );
              })}
              {!linkedEvents.length && <div className="photo-selection-state">Este produto ainda não possui eventos vinculados.</div>}
            </div>
          </section>
        )}

        {activeItem && (
          <section className="photo-selection-workspace" aria-labelledby="photos-title">
            <div className="photo-gallery-column">
              <div className="photo-section-heading photo-gallery-heading">
                <span className="photo-step-number">03</span>
                <div>
                  <h2 id="photos-title">{editable ? "Fotos disponíveis" : "Composição selecionada"}</h2>
                  <p>{activeEvent ? `${activeEvent.eventName || "Evento"} · ${formatDate(activeEvent.eventDate)}` : "Selecione um evento publicado."}</p>
                </div>
                {selectionStatus && <span className={`photo-status-badge status-${selectionStatus.toLowerCase()}`}>{SELECTION_STATUS[selectionStatus] || selectionStatus}</span>}
              </div>

              {summaryLoading && <div className="photo-selection-state" role="status">Atualizando seleção...</div>}
              {galleryError && <div className="photo-selection-state is-error" role="alert">{galleryError}</div>}

              {!editable && !summaryLoading && (
                <div className="photo-readonly-note" role="status"><FiCheckCircle aria-hidden="true" /><span><strong>{selectionStatus === "SELECTION_SENT" ? "Aguardando revisão administrativa" : "Seleção em modo de leitura"}</strong>Somente a folha escolhida permanece visível neste estado.</span></div>
              )}

              {editable && activeEvent && (
                <>
                  <div className="photo-gallery-toolbar">
                    <span>
                      {galleryLoading && gallery.length === 0
                        ? "Buscando fotos autorizadas..."
                        : `${gallery.length} foto(s) carregada(s)`}
                    </span>
                    <div className="photo-gallery-filters" role="group" aria-label="Filtrar fotos">
                      {GALLERY_FILTERS.map((filter) => (
                        <button
                          type="button"
                          key={filter.value || "all"}
                          className={galleryCategory === filter.value ? "is-active" : ""}
                          onClick={() => selectGalleryCategory(filter.value)}
                          aria-pressed={galleryCategory === filter.value}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="photo-gallery-grid" aria-busy={galleryLoading}>
                    {galleryLoading && gallery.length === 0 && Array.from({ length: 6 }, (_, index) => (
                      <div className="photo-gallery-card photo-gallery-skeleton" key={`photo-skeleton-${index}`} aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </div>
                    ))}
                    {gallery.map((photo, index) => {
                      const alreadySelected = selectedPhotoIds.has(photo.photoId);
                      const name = photoName(photo, `Foto ${index + 1}`);
                      return (
                        <article className={`photo-gallery-card${alreadySelected ? " is-selected" : ""}`} key={photo.photoId}>
                          <button
                            type="button"
                            className="photo-card-image"
                            onClick={(event) => openPreview(photo, event.currentTarget, "gallery")}
                            aria-label={`Visualizar ${name}`}
                            {...protectedImageProps}
                          >
                            <AuthenticatedMedia
                              path={thumbnailPath(photo)}
                              alt={name}
                              loading="lazy"
                              decoding="async"
                              fallback={<span className="photo-protected-fallback"><FiShield aria-hidden="true" />Foto protegida</span>}
                              {...protectedImageProps}
                            />
                            {alreadySelected && <span className="photo-selected-mark"><FiCheck aria-hidden="true" /> Na seleção</span>}
                          </button>
                          <div className="photo-card-copy">
                            <strong>{name}</strong>
                            <span>{photoAudienceLabel(photo, isUser)}</span>
                          </div>
                          <div className="photo-card-actions">
                            <button type="button" className="photo-view-button" onClick={(event) => openPreview(photo, event.currentTarget, "gallery")}><FiEye aria-hidden="true" />Visualizar</button>
                            <button
                              type="button"
                              className="photo-add-button"
                              onClick={() => addPhoto(photo.photoId)}
                              disabled={!canEditCurrentEvent || alreadySelected || !canAddMore || Boolean(actionLoading)}
                            >
                              {actionLoading === `add:${photo.photoId}` ? "Selecionando..." : alreadySelected ? "Selecionada" : "Selecionar"}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  {!galleryLoading && gallery.length === 0 && !galleryError && (
                    <div className="photo-selection-state photo-gallery-empty">
                      <FiImage aria-hidden="true" />
                      <strong>Nenhuma foto disponível para você neste evento</strong>
                      <span>Aqui aparecem somente fotos publicadas que reconheceram você ou foram liberadas para todo o evento.</span>
                    </div>
                  )}
                  {galleryPage < galleryTotalPages && (
                    <button type="button" className="photo-load-more" onClick={loadMore} disabled={galleryLoading}>{galleryLoading ? "Carregando..." : "Carregar mais"}</button>
                  )}
                </>
              )}
            </div>

            <aside className="photo-selection-sheet" aria-label="Folha de seleção">
              <div className="photo-sheet-heading">
                <div><span>Folha de seleção</span><h2>{productName(activeItem)}</h2></div>
                <strong>{selectedCount}<small>/{requiredCount}</small></strong>
              </div>
              <div className="photo-sheet-list">
                {selected.map((selection, index) => (
                  <article className="photo-sheet-item" key={selection.selectionId}>
                    <span className="photo-sheet-position">{String(index + 1).padStart(2, "0")}</span>
                    <button type="button" className="photo-sheet-thumb" onClick={(event) => openPreview(selection, event.currentTarget, "selected")} aria-label={`Visualizar ${photoName(selection, `foto ${index + 1}`)}`} {...protectedImageProps}>
                      <AuthenticatedMedia path={thumbnailPath(selection)} alt="" fallback={<FiImage aria-hidden="true" />} {...protectedImageProps} />
                    </button>
                    <span className="photo-sheet-copy"><strong>{photoName(selection, `Foto ${index + 1}`)}</strong><small>{selection.event?.name || "Evento"}</small></span>
                    {editable && (
                      <span className="photo-sheet-actions">
                        <button type="button" onClick={() => movePhoto(selection.selectionId, -1)} disabled={index === 0 || !canEditCurrentEvent || Boolean(actionLoading)} aria-label={`Mover foto ${index + 1} para cima`}><FiArrowUp aria-hidden="true" /></button>
                        <button type="button" onClick={() => movePhoto(selection.selectionId, 1)} disabled={index === selected.length - 1 || !canEditCurrentEvent || Boolean(actionLoading)} aria-label={`Mover foto ${index + 1} para baixo`}><FiArrowDown aria-hidden="true" /></button>
                        <button type="button" onClick={() => removePhoto(selection)} disabled={!canEditCurrentEvent || Boolean(actionLoading)} aria-label={`Remover foto ${index + 1}`}><FiTrash2 aria-hidden="true" /></button>
                      </span>
                    )}
                    {summary?.downloadAvailable && (
                      <button type="button" className="photo-download-one" onClick={() => downloadFile(`/media/sales/${activeSale.id}/items/${activeItem.id}/photos/${selection.eventPhotoId}/download`, `${selection.eventPhotoId}.jpg`)} aria-label={`Baixar foto ${index + 1}`}><FiDownload aria-hidden="true" /></button>
                    )}
                  </article>
                ))}
                {!selected.length && <div className="photo-sheet-empty"><FiImage aria-hidden="true" /><span>As fotos adicionadas aparecem aqui na ordem da composição.</span></div>}
              </div>
              <div className="photo-sheet-summary">
                <div><span>{selectedCount} de {requiredCount}</span><strong>{remainingCount ? `Faltam ${remainingCount}` : "Quantidade completa"}</strong></div>
                <div
                  className="photo-sheet-progress"
                  role="progressbar"
                  aria-label={`${selectedCount} de ${requiredCount} fotos selecionadas`}
                  aria-valuemin="0"
                  aria-valuemax={requiredCount}
                  aria-valuenow={selectedCount}
                >
                  <span style={{ width: `${selectionProgress}%` }} />
                </div>
                {pendingEventCount > 0 && <p><FiAlertTriangle aria-hidden="true" />{pendingEventCount} evento(s) ainda aguardam publicação.</p>}
                {isUser && editable && (
                  <button type="button" className="photo-confirm-button" onClick={submitSelection} disabled={!summary?.canSubmitSelection || Boolean(actionLoading)}>{actionLoading === "submit" ? "Enviando..." : "Confirmar e enviar"}</button>
                )}
                {isAdmin && selectionStatus === "SELECTION_SENT" && (
                  <div className="photo-admin-review">
                    <button type="button" className="photo-confirm-button" onClick={() => reviewSelection("SELECTION_APPROVED")} disabled={Boolean(actionLoading)}>Aprovar seleção</button>
                    <button type="button" className="photo-adjust-button" onClick={() => reviewSelection("ADJUSTMENT_REQUESTED")} disabled={Boolean(actionLoading)}>Solicitar ajuste</button>
                  </div>
                )}
                {summary?.downloadAvailable && (
                  <button type="button" className="photo-download-zip" onClick={() => downloadFile(`/media/sales/${activeSale.id}/items/${activeItem.id}/download.zip`, `${activeItem.id}.zip`)} disabled={Boolean(actionLoading)}><FiDownload aria-hidden="true" />Baixar seleção em .zip</button>
                )}
              </div>
            </aside>
          </section>
        )}
      </main>

      {preview && (
        <div className="selection-page-preview-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closePreview()}>
          <section className="photo-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="protected-preview-title" ref={previewDialogRef} onContextMenu={(event) => event.preventDefault()} onDragStart={(event) => event.preventDefault()}>
            <header className="photo-preview-header">
              <div className="photo-preview-copy">
                <span className="photo-preview-kicker">Visualização protegida</span>
                <h2 id="protected-preview-title">{photoName(preview)}</h2>
                <p className="photo-preview-event">
                  <FiCalendar aria-hidden="true" />
                  <strong>{previewEventName}</strong>
                  <span aria-hidden="true">•</span>
                  <span>{formatDate(previewEventDate)}</span>
                </p>
                {previewIndex >= 0 && previewItems.length > 1 && (
                  <p className="photo-preview-navigation" aria-live="polite">
                    Foto {previewIndex + 1} de {previewItems.length} · Use ← e → para navegar
                  </p>
                )}
              </div>
              <button
                type="button"
                className="photo-preview-close"
                onClick={closePreview}
                ref={previewCloseRef}
                aria-label="Fechar visualização"
              >
                <FiX aria-hidden="true" />
                <span>Fechar</span>
              </button>
            </header>
            <div className="selection-page-preview-stage" {...protectedImageProps}>
              <AuthenticatedMedia
                path={previewPath(preview)}
                alt={`Preview com marca d’água de ${photoName(preview)}`}
                keepPrevious
                loadingFallback={<div className="photo-preview-loading" role="status">Carregando preview...</div>}
                fallback={<div className="photo-preview-unavailable"><FiShield aria-hidden="true" /><strong>Preview protegido indisponível</strong><span>A versão marcada não está disponível. Nenhuma outra fonte será exibida.</span></div>}
                {...protectedImageProps}
              />
            </div>
            {editable && preview.photoId && !selectedPhotoIds.has(preview.photoId) && (
              <footer>
                <button type="button" className="photo-add-button" onClick={async () => { await addPhoto(preview.photoId); closePreview(); }} disabled={!canEditCurrentEvent || !canAddMore || Boolean(actionLoading)}><FiPlus aria-hidden="true" />Selecionar foto</button>
              </footer>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default SelecaoFotos;
